import { Component, OnInit, OnDestroy } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

import { CompSearchExecution, PrimaryFunctions } from '@xbs/xbs-enums';

import {
  CompSearchService,
  NotificationService,
  PbasService,
  SessionService,
} from '../../../../shared/services';
import { ModalService, ConfirmationModalFormComponent } from '../../../../shared/modules/modal';
import {
  ClosableModalWithErrorResponseModel
} from '../../../../shared/components/base/closable-modal-with-error-response-model';
import { TranslationConstants } from 'src/app/shared/constants';
import {
  IndustrialCodeValue,
  EntityValidationErrorResponse,
  CompSearchContext,
  MajorServiceClassification
} from 'src/app/shared/models';
import { CompSearchPayloadOverrides } from 'src/app/shared/models/comp-search-payload-overrides';
import { ConfirmationFormData } from '../../../../shared/models/confirmation-form-data';
import { ConfirmationModalService } from '../../../../shared/services/confirmation-modal.service';
import { CompSearchExecutionContext } from '../../../../shared/models/comp-search-execution-context';

@Component({
  selector: 're-cycle-search-modal',
  templateUrl: './re-cycle-search-modal.component.html',
  styleUrls: ['./re-cycle-search-modal.component.css']
})
export class ReCycleSearchModalComponent
  extends ClosableModalWithErrorResponseModel<EntityValidationErrorResponse>
  implements OnInit, OnDestroy {

  data;
  containerId: number;
  pbaId: number;
  compSearchId: number;
  jurisdictionId: number;
  jurisdictionIdToCopy: number;
  primaryFunctionId: number;
  locations: { name: string, locationId: number, locationTypeId: number, preference: number }[];
  overrides: CompSearchPayloadOverrides;
  numCompsInPool: string;
  numCompsInPoolWithDiscards: string;
  isCompPoolAllowed: boolean;
  isCompPoolAllowedToolTip: string;
  isCompPoolAllowedValuesToolTip: string;
  distributorSICCodes: IndustrialCodeValue[];
  primaryFunctions;
  isUpdatingCompPoolCount: boolean;
  runWithFionaConfirmationModalData: ConfirmationFormData;
  runWithOverridesConfirmationModalData: ConfirmationFormData;
  confirmTextModal: Function;
  isDistributor: boolean;
  testedPartyMajorServiceClassifications: MajorServiceClassification[];

  constructor(
    protected notificationService: NotificationService,
    protected modalService: ModalService,
    protected confirmationModalService: ConfirmationModalService,
    private _compSearchService: CompSearchService,
    private _pbasService: PbasService,
    private _sessionService: SessionService,
    private _translateService: TranslateService,
  ) {
    super(notificationService, modalService);

    this.overrides = new CompSearchPayloadOverrides();
    this.distributorSICCodes = [];
    this.primaryFunctions = PrimaryFunctions;
    this.isUpdatingCompPoolCount = false;
  }

  ngOnInit(): void {
    this.containerId = this._sessionService.getWorkingContainer().containerId;
    this.pbaId = this.data.pbaId;
    this.compSearchId = this.data.compSearchId;
    this.jurisdictionId = this.data.jurisdictionId;
    this.jurisdictionIdToCopy = this.data.jurisdictionIdToCopy;
    this.primaryFunctionId = PrimaryFunctions.ByType.Distributor.includes(this.data.primaryFunctionId) ? PrimaryFunctions.ByName.Distributor.Id : PrimaryFunctions.ByName.ServiceProvider.Id;
    this.overrides.industrialCodeTypeId = this.primaryFunctions.ById[this.primaryFunctionId].industrialCodeTypeId;
    this.isDistributor = PrimaryFunctions.ByType.Distributor.includes(this.primaryFunctionId);
    this.getReCycleSearchData();

    if (this.isDistributor) {
      this.getTypeOfOptions();
    } else {
      this.getMajorServiceClassificationsForTestedParty();
    }

    this.runWithFionaConfirmationModalData = {
      title: 'Warning',
      content: 'Running with Fiona will delete all work you have done in this comp search. Are you sure you want to proceed?',
      confirmationText: 'Fiona'
    };

    this.runWithOverridesConfirmationModalData = {
      title: 'Warning',
      content: 'Running with Overrides will delete all work you have done in this comp search. Are you sure you want to proceed?',
      confirmationText: 'Fiona'
    };
  }

  onLocationChange(): void {
    const locationIdAsNumber = +this.overrides.locationId;
    this.overrides.locationId = locationIdAsNumber === 0 ? null : locationIdAsNumber;

    const matchedLocation = this.locations.find((location) => {
      return location.locationId === this.overrides.locationId;
    });

    this.overrides.locationTypeId = matchedLocation ? matchedLocation.locationTypeId : null;
    this.projectCompPoolCount();
  }

  onDateChange(): void {
    this.projectCompPoolCount();
  }

  onRunWithOverrides(): void {
    if (this.isSaveDisabled) {
      return;
    }

    this.isSaveDisabled = true;

    this.responseErrors = [];
    const errorMessages = this.validateCyclingOverrides();
    if (errorMessages.length) {
      this.responseErrors = errorMessages;
      this.isSaveDisabled = false;
      return;
    }

    const context: CompSearchContext = CompSearchContext.new(this);
    const executionContext = this.buildExecutionContextWithCopyForward();
    context.setExecutionContext(executionContext);

    this.success(context);

    this.isSaveDisabled = false;
    this.closeAllModals();
  }

  onRunWithFiona(): void {

    if (this.isSaveDisabled) {
      return;
    }

    this.isSaveDisabled = true;

    const context: CompSearchContext = CompSearchContext.new(this);
    this.success(context);

    this.isSaveDisabled = false;
    this.closeAllModals();
  }

  close(): void {
    this.closeAllModals();
  }

  confirmationOverridesTextModal() {
    this.confirmationTextModal(this.runWithOverridesConfirmationModalData, this.onRunWithOverrides);
  }

  confirmationFionaTextModal() {
    this.confirmationTextModal(this.runWithFionaConfirmationModalData, this.onRunWithFiona);
  }

  confirmationTextModal(data, cb) {
    this.modalService.openModal({
      component: ConfirmationModalFormComponent,
      success: () => {
        if (cb) {
          return cb.call(this);
        }
      },
      data,
    });
  }

  projectCompPoolCount(): void {
    this.responseErrors = [];
    const errorMessages = this.validateCyclingOverrides();
    if (errorMessages.length) {
      this.responseErrors = errorMessages;
      return;
    }

    const executionContext = this.buildExecutionContext();
    const payload = { jurisdictionId: this.jurisdictionId, executionContext };

    this.isUpdatingCompPoolCount = true;
    this.isCompPoolAllowedValuesToolTip = '';
    this.numCompsInPool = 'Updating...';

    this._compSearchService
      .projectCompPoolCount(this.compSearchId, this.jurisdictionId, payload)
      .subscribe(({ data }) => {
        this.isUpdatingCompPoolCount = false;

        this.numCompsInPool = data.numCompsInPool + '';
        this.isCompPoolAllowed = data.isCompPoolAllowed;
        this.isCompPoolAllowedToolTip = (
          data.isCompPoolAllowed ? 'comp_search.is_comp_pool_allowed_true' : 'comp_search.is_comp_pool_allowed_false'
        );
        this.isCompPoolAllowedValuesToolTip = data.isCompPoolAllowed ? '' : 'comp_search.is_comp_pool_allowed_false';
      });
  }

  private getReCycleSearchData(): void {
    this._compSearchService
      .getReCycleSearchData(this.compSearchId, this.jurisdictionId)
      .subscribe(({ data }) => {
        this.locations = data.locations;
        this.overrides.locationId = data.locationId;
        this.overrides.locationTypeId = data.locationTypeId;
        this.overrides.minIndustrialCodeRange = data.minIndustrialCodeRange;
        this.overrides.maxIndustrialCodeRange = data.maxIndustrialCodeRange;
        this.overrides.majorClassificationIds = data.majorClassifications.map(classification => classification.majorServiceClassificationId);

        const utcDate = new Date(data.runOnDate);
        this.overrides.runOnDate = new Date(utcDate.getUTCFullYear(), utcDate.getUTCMonth(), utcDate.getUTCDate());

        this.numCompsInPool = data.numCompsInPool + '';
        this.numCompsInPoolWithDiscards = data.numCompsInPoolWithDiscards + '';
        this.isCompPoolAllowed = data.isCompPoolAllowed;
        this.isCompPoolAllowedToolTip = (
          this.isCompPoolAllowed ? 'comp_search.is_comp_pool_allowed_true' : 'comp_search.is_comp_pool_allowed_false'
        );
        this.isCompPoolAllowedValuesToolTip = this.isCompPoolAllowed ? '' : 'comp_search.is_comp_pool_allowed_false';
      });
  }

  private getTypeOfOptions(): void {
    this._pbasService.getTypeOfOptions(this.primaryFunctionId)
      .subscribe(({ data }) => {
        this.distributorSICCodes = data[1].codeTypes[0].subtypes;
        this.setSICCodeLabels();
      });
  }

  private setSICCodeLabels(): void {
    this.distributorSICCodes.forEach((SICCode) => {
      SICCode.displayLabel = `${SICCode.value} - ${this._translateService.instant(SICCode.name)}`;
    });
  }

  private validateCyclingOverrides(): string[] {
    const errorMessages = [];

    if (!this.overrides.locationId) {
      errorMessages.push(TranslationConstants.ERROR_COUNTRY_OR_REGION_IS_REQUIRED);
    }

    if (this.isDistributor) {
      if (!this.overrides.minIndustrialCodeRange) {
        errorMessages.push(TranslationConstants.ERROR_SIC_CODE_MIN_IS_REQUIRED);
      }

      if (!this.overrides.maxIndustrialCodeRange) {
        errorMessages.push(TranslationConstants.ERROR_SIC_CODE_MAX_IS_REQUIRED);
      }

      if (
        this.overrides.minIndustrialCodeRange &&
        this.overrides.minIndustrialCodeRange > this.overrides.maxIndustrialCodeRange
      ) {
        errorMessages.push(TranslationConstants.ERROR_MAX_SIC_MUST_BE_GREATER_THAN_MIN_SIC);
      }
    } else {
      if (!this.overrides.majorClassificationIds || this.overrides.majorClassificationIds.length === 0) {
        errorMessages.push(TranslationConstants.MAJOR_CLASSIFICATION_REQUIRED);
      }
    }

    return errorMessages;
  }

  private buildExecutionContext(): CompSearchExecutionContext {
    return {
      jurisdictions: {
        [this.jurisdictionId]: {
          executionMode: CompSearchExecution.ByName.Override.Id,
          jurisdictionId: this.jurisdictionId,
          compPoolOverrides: {
            industrialCodeTypeId: this.primaryFunctions.ById[this.primaryFunctionId].industrialCodeTypeId,
            locationId: this.overrides.locationId,
            locationTypeId: this.overrides.locationTypeId,
            minIndustrialCodeRange: this.overrides.minIndustrialCodeRange,
            maxIndustrialCodeRange: this.overrides.maxIndustrialCodeRange,
            majorClassificationIds: this.overrides.majorClassificationIds,
            runOnDate: this.overrides.runOnDate
          }
        }
      }
    };
  }

  private buildExecutionContextWithCopyForward(): CompSearchExecutionContext {
    const executionContext: CompSearchExecutionContext = this.buildExecutionContext();

    if (this.jurisdictionIdToCopy) {
      executionContext.jurisdictions[this.jurisdictionIdToCopy] = {
        executionMode: CompSearchExecution.ByName.Copy.Id,
        jurisdictionId: this.jurisdictionIdToCopy,
        compPoolOverrides: undefined
      };
    }

    return executionContext;
  }

  private getMajorServiceClassificationsForTestedParty(): void {
    this._pbasService.listMajorServiceClassificationsForTestedParty(this.pbaId).subscribe(({ data }) => {
      this.testedPartyMajorServiceClassifications = data;

      if (this.testedPartyMajorServiceClassifications) {
        this.testedPartyMajorServiceClassifications.forEach((majorServiceClassification) => {
          majorServiceClassification.name = this._translateService.instant(majorServiceClassification.name);
        });
      }
    });
  }
}
